"use client";

import { JSX, useCallback, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { LegendScale } from "@/components/charts/legend";
import {
  chartScaleColor,
  chartScaleForeground,
  chartScaleGradientStops,
  chartScaleGrayscaleStyle,
  chartScaleStepForRatio,
} from "@/components/charts/chart-scale";
import { autoContrastColor } from "@/components/charts/resolve-css-color";
import {
  type HeatmapCellSelectDetail,
  type HeatmapColumn,
  type HeatmapProjectionValue,
  type HeatmapTooltipData,
  HeatmapCells,
  HeatmapChart,
  HeatmapInteractionBoundary,
  HeatmapInteractionProvider,
  HeatmapTooltip,
  HeatmapXAxis,
  HeatmapYAxis,
  heatmapProjectionFits,
  useHeatmapInteraction,
} from "@/components/charts/heatmap";
import {
  type ChartView,
  ChartViewToggle,
  chartViewToMode,
} from "@/components/charts/projection/chart-view-toggle";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import { fmtNum } from "@/lib/format";
import { useViewerLanguage } from "@/lib/i18n";
import { emitModuleInteraction } from "@/lib/module-interaction";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { MODULE_LABEL_WRAP } from "./module-label-budget";
import { importsTokenScope } from "./imports-token-scope";
import { negativeCellColor } from "./correlation-colors";

export type CorrelationHeatmapData = {
  features: string[];
  matrix: number[][];
};

const DEFAULT_DATA: CorrelationHeatmapData = {
  features: ["Freq", "Recency", "Depth", "Session", "Retention", "Referral", "Country"],
  matrix: [
    [1.0, 0.72, 0.58, 0.41, 0.65, 0.23, 0.11],
    [0.72, 1.0, 0.44, 0.38, 0.81, 0.15, 0.05],
    [0.58, 0.44, 1.0, 0.62, 0.53, 0.21, -0.04],
    [0.41, 0.38, 0.62, 1.0, 0.37, 0.09, 0.16],
    [0.65, 0.81, 0.53, 0.37, 1.0, 0.28, 0.08],
    [0.23, 0.15, 0.21, 0.09, 0.28, 1.0, -0.12],
    [0.11, 0.05, -0.04, 0.16, 0.08, -0.12, 1.0],
  ],
};

type CellPos = { row: number; col: number };

const MATRIX_EXTENT = 288;
const MAX_CELL = 46;
const LEFT_PAD = 76;
const TOP_PAD = 64;
const RIGHT_PAD = 14;
const BOTTOM_PAD = 10;
const CELL_GAP = 4;
const CELL_INSET = CELL_GAP / 2;
const CHART_MARGIN = {
  top: TOP_PAD - CELL_INSET,
  right: RIGHT_PAD - CELL_INSET,
  bottom: BOTTOM_PAD + CELL_INSET,
  left: LEFT_PAD + CELL_INSET,
};
export const CORRELATION_FRAME_PAD = {
  top: TOP_PAD,
  right: RIGHT_PAD,
  bottom: BOTTOM_PAD,
  left: LEFT_PAD,
} as const;

export function correlationFrameSize(n: number): {
  cellSize: number;
  matrixPx: number;
  width: number;
  height: number;
} {
  const cellSize = n > 0 ? Math.min(MAX_CELL, MATRIX_EXTENT / n) : MAX_CELL;
  const matrixPx = cellSize * n;
  return {
    cellSize,
    matrixPx,
    width: LEFT_PAD + matrixPx + RIGHT_PAD,
    height: TOP_PAD + matrixPx + BOTTOM_PAD,
  };
}

export const CORRELATION_DEFAULT_FEATURE_COUNT = DEFAULT_DATA.features.length;

const INACTIVE_OPACITY = 0.35;
const COLUMN_LABEL_ANGLE = -45;
const PLACEHOLDER_DATE = new Date(0);

function clampCorrelation(raw: unknown): number {
  const value = Number(raw);
  return Number.isFinite(value) ? Math.max(-1, Math.min(1, value)) : 0;
}

function cellFill(val: number, grayscale?: boolean): string {
  if (val < 0) {
    return negativeCellColor(val, grayscale);
  }
  return chartScaleColor(chartScaleStepForRatio(val));
}

/**
 * Text color follows the cell it sits on: paired scale tokens for the ramp,
 * measured luminance for the negative hue, which is a concrete color string.
 */
function cellTextColor(val: number, grayscale?: boolean): string {
  if (val < 0) {
    return (
      autoContrastColor(negativeCellColor(val, grayscale)) ??
      "var(--foreground)"
    );
  }
  return chartScaleForeground(chartScaleStepForRatio(val));
}

function toColumns(features: string[], matrix: number[][]): HeatmapColumn[] {
  return features.map((_, col) => ({
    bin: col,
    bins: features.map((__, row) => ({
      bin: row,
      count: clampCorrelation(matrix[row]?.[col]),
      date: PLACEHOLDER_DATE,
    })),
  }));
}

const correlationMagnitude: HeatmapProjectionValue = (count, column, row) =>
  column === row ? 0 : Math.abs(count);

const formatCorrelation = (value: number, locale?: string): string =>
  fmtNum(value, 2, locale);

function CorrelationDetail({
  features,
  matrix,
  selected,
  trailing,
}: {
  features: string[];
  matrix: number[][];
  selected: CellPos | null;
  trailing?: JSX.Element | null;
}): JSX.Element {
  const { hoveredCell } = useHeatmapInteraction();
  const { t, intlLocale } = useViewerLanguage();
  const active: CellPos | null = hoveredCell
    ? { row: hoveredCell.row, col: hoveredCell.column }
    : selected;
  const raw = active ? matrix[active.row]?.[active.col] : undefined;
  const activeVal = raw === undefined ? undefined : clampCorrelation(raw);

  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3",
        trailing ? "min-h-8" : "min-h-[1.25rem]",
      )}
    >
      <div className="min-w-0 text-xs text-[var(--muted-foreground)]">
        {active !== null && activeVal !== undefined ? (
          <>
            <span className="mr-2 text-[10px] text-[var(--subtle)]">
              {t("Detail")}
            </span>
            <span
              className={cn(
                "font-medium text-[var(--foreground)]",
                MODULE_LABEL_WRAP.label,
              )}
            >
              {stripInlineMarkdown(features[active.row])}
            </span>
            {" × "}
            <span
              className={cn(
                "font-medium text-[var(--foreground)]",
                MODULE_LABEL_WRAP.label,
              )}
            >
              {stripInlineMarkdown(features[active.col])}
            </span>
            {" · "}
            <span className="font-medium tabular-nums text-[var(--foreground)]">
              {formatCorrelation(activeVal, intlLocale)}
            </span>
          </>
        ) : (
          <span className="text-[var(--subtle)]">
            {t("Select a cell for pair detail")}
          </span>
        )}
      </div>
      {trailing}
    </div>
  );
}

export default function ModuleCorrelationHeatmap({
  view3d,
  data = DEFAULT_DATA,
  grayscale,
  accent,
  className,
}: {
  view3d?: boolean;
  data?: CorrelationHeatmapData;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const { t, intlLocale } = useViewerLanguage();
  const features = useMemo(() => data.features ?? [], [data.features]);
  const matrix = useMemo(() => data.matrix ?? [], [data.matrix]);
  const n = features.length;
  const columns = useMemo(() => toColumns(features, matrix), [features, matrix]);
  const labels = useMemo(
    () => features.map((feature) => stripInlineMarkdown(feature)),
    [features],
  );
  const [selected, setSelected] = useState<CellPos | null>(null);
  const [view, setView] = useState<ChartView>("3d");
  const canProject = Boolean(view3d) && heatmapProjectionFits(n, n);
  const projection = canProject ? chartViewToMode(view) : undefined;

  const { cellSize, width, height } = correlationFrameSize(n);

  const colorScale = useCallback(
    (count: number | null | undefined) => cellFill(Number(count ?? 0), grayscale),
    [grayscale],
  );
  const valueLabelColor = useCallback(
    (count: number) => cellTextColor(count, grayscale),
    [grayscale],
  );
  const formatValue = useCallback(
    (value: number) => formatCorrelation(value, intlLocale),
    [intlLocale],
  );
  const formatTitle = useCallback(
    ({ column, row }: HeatmapTooltipData) =>
      `${labels[row] ?? ""} × ${labels[column] ?? ""}`,
    [labels],
  );

  const handleCellSelect = useCallback(
    ({ column, row, selected: isSelected, element }: HeatmapCellSelectDetail) => {
      setSelected(isSelected ? { row, col: column } : null);
      emitModuleInteraction(element, isSelected ? "select" : "deselect", {
        kind: "cell",
        id: `${row}:${column}`,
        index: row * n + column,
      });
    },
    [n],
  );

  return (
    <div
      className={cn("flex w-full flex-col gap-3", className)}
      style={{ ...chartScaleGrayscaleStyle(grayscale), ...importsTokenScope(grayscale, accent) }}
    >
      <HeatmapInteractionProvider>
        <HeatmapInteractionBoundary className="flex flex-col gap-3">
          <CorrelationDetail
            features={features}
            matrix={matrix}
            selected={selected}
            trailing={
              canProject ? (
                <ChartViewToggle
                  className="shrink-0"
                  kind="heatmap"
                  onChange={setView}
                  value={view}
                />
              ) : null
            }
          />

          <SwipeScroll className="relative w-full">
            <div className="shrink-0" style={{ width, height }}>
              {n > 0 ? (
                <HeatmapChart
                  binSize={cellSize}
                  colorScale={colorScale}
                  data={columns}
                  gap={CELL_GAP}
                  margin={CHART_MARGIN}
                  projection={projection}
                  projectionMax={1}
                  projectionValue={correlationMagnitude}
                >
                  <HeatmapCells
                    cornerRadius={3}
                    formatValueLabel={formatValue}
                    hideGhostCells={false}
                    hoverMode="crosshair"
                    inactiveOpacity={INACTIVE_OPACITY}
                    onCellSelect={handleCellSelect}
                    valueLabelColor={valueLabelColor}
                    valueLabels
                  />
                  <HeatmapXAxis
                    highlightHovered
                    labelAngle={COLUMN_LABEL_ANGLE}
                    labels={labels}
                  />
                  <HeatmapYAxis highlightHovered labels={labels} />
                  <HeatmapTooltip
                    formatLabel={formatValue}
                    formatSubtitle={() => null}
                    formatTitle={formatTitle}
                  />
                </HeatmapChart>
              ) : null}
            </div>
          </SwipeScroll>
        </HeatmapInteractionBoundary>
      </HeatmapInteractionProvider>

      <LegendScale
        caption={t("Correlation")}
        gradient={`linear-gradient(to right, ${negativeCellColor(-1, grayscale)} 0%, ${negativeCellColor(0, grayscale)} 45%, ${chartScaleGradientStops(50, 100).join(", ")})`}
        startLabel="−1"
        endLabel="+1"
        rampClassName="h-2 min-w-16 max-w-32 flex-1 rounded-full"
      />
    </div>
  );
}
